import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUtils';

interface CampaignImageProps {
  imageUrl?: string | null;
  alt?: string;
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  showFallback?: boolean;
}

export const CampaignImage: React.FC<CampaignImageProps> = ({
  imageUrl,
  alt = 'Imagem da campanha',
  width = '100%',
  height = 200,
  borderRadius = 1,
  objectFit = 'cover',
  showFallback = true
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  console.log('🖼️ CampaignImage renderizado com:', {
    imageUrl,
    alt,
    width,
    height,
    imageError,
    isLoading
  });

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('❌ CampaignImage - Erro ao carregar imagem:');
    console.error('❌ ImageUrl original:', imageUrl);
    console.error('❌ URL processada:', getImageUrl(imageUrl));
    console.error('❌ Evento de erro:', e);
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    console.log('✅ CampaignImage - Imagem carregada com sucesso:');
    console.log('✅ ImageUrl original:', imageUrl);
    console.log('✅ URL processada:', getImageUrl(imageUrl));
    setIsLoading(false);
  };

  // Se não há URL de imagem ou houve erro, mostrar fallback
  if (!imageUrl || imageError) {
    console.log('🔄 CampaignImage - Mostrando fallback:', { imageUrl, imageError, showFallback });
    
    if (!showFallback) return null;
    
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius,
          backgroundColor: 'grey.100',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed',
          borderColor: 'grey.300'
        }}
      >
        <ImageIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {imageError ? 'Erro ao carregar' : 'Sem imagem'}
        </Typography>
        {imageError && (
          <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem', textAlign: 'center', px: 1 }}>
            URL: {imageUrl}
          </Typography>
        )}
      </Box>
    );
  }

  const processedUrl = getImageUrl(imageUrl);
  console.log('🎯 CampaignImage - Tentando carregar imagem:', processedUrl);

  // Determinar se a altura é automática
  const isAutoHeight = height === 'auto';

  return (
    <Box
      sx={{
        width,
        height: isAutoHeight ? 'auto' : height,
        borderRadius,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: isLoading ? 'grey.100' : 'transparent',
        maxWidth: '100%'
      }}
    >
      {isLoading && !isAutoHeight && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            zIndex: 1
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Carregando...
          </Typography>
        </Box>
      )}
      
      <img
        src={processedUrl}
        alt={alt}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{
          width: '100%',
          height: isAutoHeight ? 'auto' : '100%',
          objectFit,
          display: 'block',
          maxWidth: '100%'
        }}
      />
    </Box>
  );
}; 